"use client";
import { useEffect, useState } from "react";
import { Amplify } from "aws-amplify";
import images from "../constants/images";
import {
  getCurrentUser,
  signInWithRedirect,
  signOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import type { AuthUser } from "aws-amplify/auth";
import { config } from "./config";

// Configure Amplify
// Note: Callback URL will be set dynamically when window is available
const getAmplifyConfig = () => {
  const callbackUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/auth/callback`
      : "";

  return {
    Auth: {
      Cognito: {
        userPoolId: config.COGNITO_USER_POOL_ID,
        userPoolClientId: config.COGNITO_USER_POOL_CLIENT_ID,
        loginWith: {
          oauth: {
            domain: `${config.COGNITO_DOMAIN}.auth.${config.AWS_REGION}.amazoncognito.com`,
            scopes: ["openid", "email"],
            redirectSignIn: [callbackUrl],
            redirectSignOut: [callbackUrl],
            responseType: "code" as const,
          },
        },
      },
    },
  };
};

if (typeof window !== "undefined") {
  Amplify.configure(getAmplifyConfig(), { ssr: true });
}

// Function to log current tokens and session info
const logCurrentTokens = async () => {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      console.log("🆔 ID Token Payload:", session.tokens.idToken.payload);
    }
  } catch (error) {
    console.error("❌ Token logging error:", error);
  }
};
function setCookie(name: string, value: string, days = 7) {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;Secure;SameSite=Lax`;
}
// 🔐 useAuth Hook
export function useAuth() {
  const [user, setUser] = useState<(AuthUser & { attributes?: any }) | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    checkAuthState();

    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      if (error) {
        console.error("❌ OAuth Error:", error, errorDescription);
        setError(`Authentication failed: ${errorDescription || error}`);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        // On sign in failure, redirect to currentPagePath if available
        if (typeof window !== "undefined") {
          const currentPagePath = sessionStorage.getItem("currentPagePath");
          if (currentPagePath && currentPagePath !== window.location.pathname) {
            setTimeout(() => {
              window.location.href = currentPagePath;
            }, 1000);
          }
        }
        setLoading(false);
        return;
      }

      if (code) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        setTimeout(() => {
          checkAuthState();
        }, 1000);
      }
    };

    if (window.location.search) {
      handleAuthCallback();
    }
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idPayload = session.tokens?.idToken?.payload;

      const firstName = "User";

      const enrichedUser = {
        ...currentUser,
        attributes: {
          ...idPayload,
          firstName,
        },
      };

      setUser(enrichedUser);
      setError("");
      if (idPayload?.sub) {
        setCookie("loginUserId", idPayload.sub); // <-- here
      }
      //   await logCurrentTokens();
    } catch (err: any) {
      //   console.log("User not authenticated:", err.message);
      setUser(null);
      if (err.name !== "UserUnAuthenticatedException") {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return { user, loading, error, checkAuthState, setError };
}

// 👉 Google Sign-In Button
function GoogleSignInButton() {
  const [loading, setLoading] = useState(false);
  const { setError } = useAuth();

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("error")) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
      await signInWithRedirect({ provider: "Google" });
    } catch (error: any) {
      console.error("Google Sign-In error:", error);
      setError(error.message || "Google Sign-In failed");
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <button
        onClick={handleGoogleSignIn}
        disabled={loading}
        className="flex items-center justify-center w-full bg-white border border-gray-300 rounded-lg px-6 py-3 text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3" />
        ) : (
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        )}
        {loading ? "Signing in..." : "Continue with Google"}
      </button>
    </div>
  );
}

// ✅ User Profile Component
function UserProfile({ user }: { user: AuthUser & { attributes?: any } }) {
  const [loading, setLoading] = useState(false);

  const handleSignOut = async () => {
    try {
      setLoading(true);

      await signOut(); // important

      const currentPagePath =
        typeof window !== "undefined"
          ? sessionStorage.getItem("currentPagePath")
          : null;

      if (typeof window !== "undefined") {
        window.location.href = currentPagePath || "/";
      }
    } catch (error) {
      console.error("Sign out error:", error);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="text-center">
        <p className="text-lg font-semibold mb-4">
          Hello, {user.attributes?.given_name || user.username}
        </p>
        <button
          onClick={handleSignOut}
          disabled={loading}
          className="w-full bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? "Signing out..." : "Sign Out"}
        </button>
      </div>
    </div>
  );
}

// 🔄 Loading UI
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

// 🎯 Main LoginPopup Component
export default function LoginPopup({ onClose }: { onClose: () => void }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Mobile Bottom Sheet */}
      <div
        className="flex md:hidden fixed inset-0 z-50 items-end bg-black/30 backdrop-blur-md"
        style={{
          WebkitBackdropFilter: "blur(8px)",
          backdropFilter: "blur(8px)",
        }}
        onClick={onClose}
      >
        <div
          className="bg-white w-full rounded-t-3xl shadow-xl relative animate-slide-up max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
          </div>

          {/* Close Icon */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-[#FE406C] text-lg z-10"
          >
            ✕
          </button>

          {/* Content */}
          <div className="px-8 pb-8 pt-4">
            {/* Logo */}
            <div className="text-center mb-6">
              <img
                src={images.logoWithName.src}
                alt="MyWeddinglyInvites"
                className="h-6 mx-auto"
              />
            </div>

            <h2 className="text-2xl font-semibold text-center mb-6">Login</h2>

            {user ? <UserProfile user={user} /> : <GoogleSignInButton />}
          </div>
        </div>
      </div>
      <div
        className="hidden md:flex fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md"
        style={{
          WebkitBackdropFilter: "blur(8px)",
          backdropFilter: "blur(8px)",
        }}
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Icon */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-[#FE406C] text-lg"
          >
            ✕
          </button>

          {/* Logo */}
          <div className="text-center mb-6">
            <img
              src={images.logoWithName.src}
              alt="MyWeddinglyInvites"
              className="h-6 mx-auto"
            />
          </div>

          <h2 className="text-2xl font-semibold text-center mb-6">Login</h2>

          {user ? <UserProfile user={user} /> : <GoogleSignInButton />}
        </div>
      </div>
    </div>
  );
}
